import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence, motion } from 'framer-motion';
import { useLenis } from './hooks/useLenis';
import { LenisProvider } from './context/LenisContext';
import Navbar from './components/navbar';
import Hero from './components/hero';
import CustomCursor from './components/CustomCursor';
import ScrollProgress from './components/ScrollProgress';
import ThankYou from './components/thankyou';
import ErrorBoundary from './components/ErrorBoundary';
import SEO from './components/SEO';

const Projects = lazy(() => import('./components/projects'));
const Skills = lazy(() => import('./components/skills'));
const Contact = lazy(() => import('./components/contact'));
const Footer = lazy(() => import('./components/footer'));

function MainContent() {
  return (
    <>
      <SEO
        title="Sebastien Toscano - AI Engineer | R&D"
        description="Sebastien Toscano is an AI Engineer in R&D"
        keywords="Sebastien Toscano, AI Engineer, R&D"
        type="website"
        url="/"
      />
      <CustomCursor />
      <ScrollProgress />
      <div className="bg-[#060d18] text-white">
        <Hero />
      </div>
      <Suspense fallback={<div className="bg-[#060d18]" />}>
        <div className="bg-[#060d18] text-white">
          <Projects />
        </div>
        <div className="bg-[#060d18] text-white">
          <Skills />
        </div>
        <div className="bg-[#060d18] text-white">
          <Contact />
        </div>
        <div className="bg-[#060d18] text-white">
          <Footer />
        </div>
      </Suspense>
    </>
  );
}

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
} as const;

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div key={location.pathname} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }}>
        <Routes location={location}>
          <Route path="/" element={<MainContent />} />
          <Route path="/thank-you" element={
            <>
              <SEO
                title="Thank You - Sebastien Toscano"
                description="Thank you for contacting Sebastien Toscano"
                type="website"
                url="/thank-you"
              />
              <ThankYou />
            </>
          } />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const lenisRef = useLenis();
  return (
    <HelmetProvider>
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </head>
        <body>
          <LenisProvider lenisRef={lenisRef}>
            <Navbar />
            <ErrorBoundary>
              <AnimatedRoutes />
            </ErrorBoundary>
          </LenisProvider>
        </body>
      </html>
    </HelmetProvider>
  );
}

export default App;
